import { useEffect, useRef } from 'react';

/**
 * Inspired by https://usehooks.com/useEventListener/
 * Useful for adding event listeners to DOM nodes outside React components
 */
const useEventListener = (
  element: EventTarget,
  type: string,
  listener: (e: Event) => void,
  { useCapture = false }: { useCapture?: boolean }
) => {
  const savedListener = useRef<((e: Event) => void) | null>(null);

  // Update ref.current value if listener changes.
  // This allows our effect below to always get latest listener ...
  // ... without us needing to pass it in effect deps array ...
  // ... and potentially cause effect to re-run every render.
  useEffect(() => {
    savedListener.current = listener;
  }, [listener]);

  useEffect(() => {
    const eventListener = (e: Event) => savedListener.current!(e);
    element.addEventListener(type, eventListener, useCapture);
    return () => element.removeEventListener(type, eventListener, useCapture);
  }, [type, element, useCapture]);
};

export default useEventListener;
